// src/pages/SensitivityAnalysis.jsx
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import API from "../../../api";

////////////////////////////////////////////////////////////////////////////////
// ─── Helper: calcFullYear ───────────────────────────────────────────────────
// Straight-line diminishing-balance depreciation logic: returns full-year
// depreciation amount for a given asset cost, ratePercent, and yearIndex.
function calcFullYear(cost, ratePercent, yearIndex) {
  if (yearIndex === 0) return 0;
  let remaining = cost;
  let depreciationForThisYear = 0;
  for (let i = 1; i <= yearIndex; i++) {
    const depThisYear = (remaining * ratePercent) / 100;
    remaining -= depThisYear;
    depreciationForThisYear = depThisYear;
  }
  return Number(depreciationForThisYear.toFixed(2));
}

////////////////////////////////////////////////////////////////////////////////
// ─── Helper: bucketEmiSchedule ──────────────────────────────────────────────
// Groups monthly EMI rows by financial year (April–March) into year summaries.
function bucketEmiSchedule(emiSchedule, startMonthStr) {
  const generateMonthLabels = (startStr, length) => {
    if (!startStr || !startStr.includes("-")) return [];
    const [startM, startY] = startStr.split("-").map(Number);
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    let m = startM - 1,
      y = startY;
    return Array.from({ length }, () => {
      const label = `${months[m]}-${y}`;
      m = (m + 1) % 12;
      if (m === 0) y++;
      return label;
    });
  };

  const monthLabels = generateMonthLabels(startMonthStr, emiSchedule.length);
  const monthsMap = {
    Jan: 1,
    Feb: 2,
    Mar: 3,
    Apr: 4,
    May: 5,
    Jun: 6,
    Jul: 7,
    Aug: 8,
    Sep: 9,
    Oct: 10,
    Nov: 11,
    Dec: 12,
  };

  const bucket = {};
  emiSchedule.forEach((row, idx) => {
    const lbl = monthLabels[idx] || "";
    const [monStr, yrStr] = lbl.split("-");
    const mon = monthsMap[monStr] || 0;
    const yr = parseInt(yrStr, 10) || 0;
    const fyStart = mon >= 4 ? yr : yr - 1;
    const fyLabel = `${fyStart}-${String(fyStart + 1).slice(-2)}`;
    if (!bucket[fyLabel]) {
      bucket[fyLabel] = {
        year: fyLabel,
        totalPrincipal: 0,
        totalInterest: 0,
        closingBalance: 0,
      };
    }
    bucket[fyLabel].totalPrincipal += row.principal;
    bucket[fyLabel].totalInterest += row.interest;
    bucket[fyLabel].closingBalance = row.closingBalance;
  });
  return Object.values(bucket);
}

export default function SensitivityAnalysis() {
  const { projectId } = useParams();

  // ─── State Hooks ─────────────────────────────────────────────────────────
  // API-driven data:
  const [projectYears, setProjectYears] = useState([]); // ["2025-26", "2026-27", ...]
  const [netProfitAfterTax, setNetProfitAfterTax] = useState([]); // [npatYear0, npatYear1, ...]
  const [inputs, setInputs] = useState(null); // full API response
  const [depreciationData, setDepreciationData] = useState([]); // [ { particular, taxPercent }, ... ]
  const [termLoanInput, setTermLoanInput] = useState({ startMonth: "" });
  const [emiYearlySummary, setEmiYearlySummary] = useState([]); // [ { year, totalPrincipal, totalInterest, closingBalance }, ... ]

  // LocalStorage-only:
  const [totalSalesNet, setTotalSalesNet] = useState([]); // totalAnnualSales array
  const [costMaterials, setCostMaterials] = useState([]); // grandTotalsCostOfProduction array

  // Derived:
  const [depreciationTotals, setDepreciationTotals] = useState([]); // computed depreciation per year

  // Scenario percentages (optionally saved in localStorage):
  const [scenario1Pct, setScenario1Pct] = useState(0);
  const [scenario2Pct, setScenario2Pct] = useState(0);

  // Loading / Error
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // ─── Fetch API & localStorage on mount ─────────────────────────────────────
  useEffect(() => {
    // 2) Scenario pct from localStorage if present
    /* const s1 = parseFloat(localStorage.getItem('sensitivityScenario1') || '0');
    const s2 = parseFloat(localStorage.getItem('sensitivityScenario2') || '0'); */
    /* setScenario1Pct(isNaN(s1) ? 0 : s1);
    setScenario2Pct(isNaN(s2) ? 0 : s2); */

    // 3) Fetch all other data from API
    API.get(`/user/project-inputs/${projectId}`)
      .then(({ data }) => {
        setInputs(data);

        // a) projectYears
        setProjectYears(
          Array.isArray(data.projectYears) ? data.projectYears : []
        );

        const totalAnnualSales = data.totalAnnualSales || "[]";
        const grandTotalCostOfProduction =
          data.grandTotalCostOfProduction || "[]";
        setTotalSalesNet(
          Array.isArray(totalAnnualSales)
            ? totalAnnualSales.map((v) => parseFloat(v) || 0)
            : []
        );
        setCostMaterials(
          Array.isArray(grandTotalCostOfProduction)
            ? grandTotalCostOfProduction.map((v) => parseFloat(v) || 0)
            : []
        );

        // b) netProfitAfterTax
        const npatArr = Array.isArray(data.netProfitAfterTax)
          ? data.netProfitAfterTax.map((v) => parseFloat(v) || 0)
          : [];
        setNetProfitAfterTax(npatArr);

        // c) depreciationData (each: { particular, taxPercent })
        setDepreciationData(
          Array.isArray(data.depreciationData) ? data.depreciationData : []
        );

        // d) termLoanInput
        setTermLoanInput(data.termLoanInput || { startMonth: "" });

        setScenario1Pct(data.sensitivityScenario1 || 0);
        setScenario2Pct(data.sensitivityScenario2 || 0);

        // e) EMI schedule → bucket into yearly summary
        const term = data.termLoanInput || {};
        let emiSchedule = Array.isArray(term.emiSchedule)
          ? term.emiSchedule
          : [];
        const rawStart =
          term.pStartMonth && term.pStartMonth.includes("-")
            ? term.pStartMonth
            : term.startMonth || "";

        // Fallback to localStorage if API returned no emiSchedule
        /* if (!emiSchedule.length) {
          const localEmi = JSON.parse(
            localStorage.getItem('emiSchedule') || '[]'
          );
          if (Array.isArray(localEmi) && localEmi.length) {
            emiSchedule = localEmi;
          }
        } */

        if (emiSchedule.length) {
          setEmiYearlySummary(bucketEmiSchedule(emiSchedule, rawStart));
        } else {
          setEmiYearlySummary([]);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch project inputs:", err);
        setError("Unable to load sensitivity analysis data.");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [projectId]);

  // ─── Depreciation: Compute months active in a given FY label ───────────────
  // Parse "mm-yyyy" → { month, year }
  const prod = useMemo(() => {
    const [m, y] = (termLoanInput.startMonth || "").split("-").map(Number);
    return m && y ? { month: m, year: y } : null;
  }, [termLoanInput.startMonth]);

  // Given an FY label "YYYY-YY", return months active since production start
  const getMonthsActive = useCallback(
    (fyLabel) => {
      if (!prod) return 12;
      const fyStart = parseInt(fyLabel.slice(0, 4), 10);
      if (isNaN(fyStart)) return 12;
      const fyEnd = fyStart + 1;
      // Number of months from production start until end of this FY:
      const raw = (fyEnd - prod.year) * 12 + (3 - prod.month + 1);
      return Math.max(0, Math.min(raw, 12));
    },
    [prod]
  );

  // ─── Cost lookup for each depreciation "particular" from API inputs ─────────
  const getCostByParticular = useCallback(
    (p) => {
      if (!inputs) return 0;
      const {
        costProjectInputs = {},
        plantMachineryData = [],
        plantContingencies,
        plantGstPercent,
        furnitureAndFixtures = [],
        otherFixedAssets = [],
        computers = [],
        preliminaryExpenses = [],
      } = inputs;

      const keyMap = {
        "Land & Site Development (On Lease)": "landDevelopment",
        "Civil Construction (On Lease)": "civilConstruction",
      };
      if (keyMap[p] && costProjectInputs[keyMap[p]] != null) {
        return parseFloat(costProjectInputs[keyMap[p]]) || 0;
      }

      switch (p) {
        case "Plant & Machinery": {
          const subtotal = plantMachineryData.reduce(
            (s, r) => s + (parseFloat(r.amount) || 0),
            0
          );
          const cont = (subtotal * (parseFloat(plantContingencies) || 0)) / 100;
          const tax =
            ((subtotal + cont) * (parseFloat(plantGstPercent) || 0)) / 100;
          return subtotal + cont + tax;
        }
        case "Furniture and Fixture":
          return furnitureAndFixtures.reduce(
            (s, r) => s + (parseFloat(r.amount) || 0),
            0
          );
        case "Miscellaneous Fixed Assets":
          return otherFixedAssets.reduce(
            (s, r) => s + (parseFloat(r.amount) || 0),
            0
          );
        case "Computer and Software":
          return computers.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
        case "Preliminary Expenses":
          return preliminaryExpenses.reduce(
            (s, r) => s + (parseFloat(r.amount) || 0),
            0
          );
        default:
          return 0;
      }
    },
    [inputs]
  );

  // ─── Compute depreciationTotals: sum of each particular's prorated depreciation ─
  useEffect(() => {
    if (!inputs || !projectYears.length || !depreciationData.length) {
      setDepreciationTotals([]);
      return;
    }

    // For each particular row, compute per-year depreciation array
    const perParticular = depreciationData.map((row) => {
      const cost = getCostByParticular(row.particular);
      const rate = parseFloat(row.taxPercent) || 0;
      const perYear = projectYears.map((fyLabel, yi) => {
        // Full-year depreciation for yearIndex = yi
        const fullYearDep = calcFullYear(cost, rate, yi);
        // Prorate by months active
        const months = getMonthsActive(fyLabel);
        return Number(((fullYearDep * months) / 12).toFixed(2));
      });
      return perYear;
    });

    // Sum across all particulars for each yearIndex
    const totals = projectYears.map((_, yi) => {
      return perParticular.reduce((acc, arr) => acc + (arr[yi] || 0), 0);
    });
    setDepreciationTotals(totals);
  }, [
    inputs,
    projectYears,
    depreciationData,
    getCostByParticular,
    getMonthsActive,
  ]);

  // ─── Build arrays for “Interest On Loan” & “Repayment of Term Loan” ─────
  const interestOnLoanFull = useMemo(() => {
    return projectYears.map((yrLabel) => {
      const found = emiYearlySummary.find((row) => row.year === yrLabel);
      return found ? parseFloat(found.totalInterest) || 0 : 0;
    });
  }, [projectYears, emiYearlySummary]);

  const repaymentLoanArrFull = useMemo(() => {
    return projectYears.map((yrLabel) => {
      const found = emiYearlySummary.find((row) => row.year === yrLabel);
      return found ? parseFloat(found.totalPrincipal) || 0 : 0;
    });
  }, [projectYears, emiYearlySummary]);

  // ─── Net Profit Before Depreciation & Interest (NPBDI) ────────────────────
  const netProfitBeforeDepIntFull = useMemo(() => {
    return projectYears.map((_, i) => {
      const npat = netProfitAfterTax[i] || 0;
      const depr = depreciationTotals[i] || 0;
      const iol = interestOnLoanFull[i] || 0;
      return Number((npat + depr + iol).toFixed(2));
    });
  }, [projectYears, netProfitAfterTax, depreciationTotals, interestOnLoanFull]);

  // ─── Total Debts = Principal + Interest ─────────────────────────────────
  const totalDebtsFull = useMemo(() => {
    return projectYears.map((_, i) => {
      const principal = repaymentLoanArrFull[i] || 0;
      const interest = interestOnLoanFull[i] || 0;
      return Number((principal + interest).toFixed(2));
    });
  }, [projectYears, repaymentLoanArrFull, interestOnLoanFull]);

  // ─── DSCR = NPBDI / Total Debts ───────────────────────────────────────────
  const dscrFull = useMemo(() => {
    return projectYears.map((_, i) => {
      const npbdi = netProfitBeforeDepIntFull[i] || 0;
      const debt = totalDebtsFull[i] || 0;
      return debt !== 0 ? Number((npbdi / debt).toFixed(2)) : 0;
    });
  }, [projectYears, netProfitBeforeDepIntFull, totalDebtsFull]);

  // ─── Scenario 1: Selling Price ↓ by scenario1Pct% ────────────────────────
  const pct1 = scenario1Pct;
  const scenarioSales1Full = useMemo(() => {
    return totalSalesNet.map((orig) =>
      Number(((orig * (100 - pct1)) / 100).toFixed(2))
    );
  }, [totalSalesNet, pct1]);

  const profitAfterTax1Full = useMemo(() => {
    return projectYears.map((_, i) => {
      const origNpat = netProfitAfterTax[i] || 0;
      const origSales = totalSalesNet[i] || 0;
      const newSales = scenarioSales1Full[i] || 0;
      return Number((origNpat + (newSales - origSales)).toFixed(2));
    });
  }, [projectYears, netProfitAfterTax, totalSalesNet, scenarioSales1Full]);

  const netProfitBeforeDepInt1Full = useMemo(() => {
    return projectYears.map((_, i) => {
      const pat1 = profitAfterTax1Full[i] || 0;
      const depr = depreciationTotals[i] || 0;
      const iol = interestOnLoanFull[i] || 0;
      return Number((pat1 + depr + iol).toFixed(2));
    });
  }, [
    projectYears,
    profitAfterTax1Full,
    depreciationTotals,
    interestOnLoanFull,
  ]);

  const dscr1Full = useMemo(() => {
    return projectYears.map((_, i) => {
      const npbdi1 = netProfitBeforeDepInt1Full[i] || 0;
      const debt = totalDebtsFull[i] || 0;
      return debt !== 0 ? Number((npbdi1 / debt).toFixed(2)) : 0;
    });
  }, [projectYears, netProfitBeforeDepInt1Full, totalDebtsFull]);

  // ─── Scenario 2: Raw Material Cost ↑ by scenario2Pct% ───────────────────
  const pct2 = scenario2Pct;
  const scenarioCostMaterials2Full = useMemo(() => {
    return costMaterials.map((origCost) =>
      Number(((origCost * (100 + pct2)) / 100).toFixed(2))
    );
  }, [costMaterials, pct2]);

  const profitAfterTax2Full = useMemo(() => {
    return projectYears.map((_, i) => {
      const origNpat = netProfitAfterTax[i] || 0;
      const origCost = costMaterials[i] || 0;
      const newCost = scenarioCostMaterials2Full[i] || 0;
      return Number((origNpat + (origCost - newCost)).toFixed(2));
    });
  }, [
    projectYears,
    netProfitAfterTax,
    costMaterials,
    scenarioCostMaterials2Full,
  ]);

  const netProfitBeforeDepInt2Full = useMemo(() => {
    return projectYears.map((_, i) => {
      const pat2 = profitAfterTax2Full[i] || 0;
      const depr = depreciationTotals[i] || 0;
      const iol = interestOnLoanFull[i] || 0;
      return Number((pat2 + depr + iol).toFixed(2));
    });
  }, [
    projectYears,
    profitAfterTax2Full,
    depreciationTotals,
    interestOnLoanFull,
  ]);

  const dscr2Full = useMemo(() => {
    return projectYears.map((_, i) => {
      const npbdi2 = netProfitBeforeDepInt2Full[i] || 0;
      const debt = totalDebtsFull[i] || 0;
      return debt !== 0 ? Number((npbdi2 / debt).toFixed(2)) : 0;
    });
  }, [projectYears, netProfitBeforeDepInt2Full, totalDebtsFull]);

  // ─── Slice off Year 0 (index 0) before rendering ─────────────────────────
  const yrs = projectYears.slice(1);
  const salesNet = totalSalesNet.slice(1);
  const costs = costMaterials.slice(1);
  const npat = netProfitAfterTax.slice(1);
  const depr = depreciationTotals.slice(1);
  const iol = interestOnLoanFull.slice(1);
  const rla = repaymentLoanArrFull.slice(1);
  const npdi = netProfitBeforeDepIntFull.slice(1);
  const deb = totalDebtsFull.slice(1);
  const ds = dscrFull.slice(1);

  const ss1 = scenarioSales1Full.slice(1);
  const pat1 = profitAfterTax1Full.slice(1);
  const npdi1 = netProfitBeforeDepInt1Full.slice(1);
  const ds1 = dscr1Full.slice(1);

  const cm2 = scenarioCostMaterials2Full.slice(1);
  const pat2 = profitAfterTax2Full.slice(1);
  const npdi2 = netProfitBeforeDepInt2Full.slice(1);
  const ds2 = dscr2Full.slice(1);

  // ─── Averages ──────────────────────────────────────────────────────────
  const avgDSCR = ds.length
    ? Number((ds.reduce((a, b) => a + b, 0) / ds.length).toFixed(2))
    : 0;
  const avgDSCR1 = ds1.length
    ? Number((ds1.reduce((a, b) => a + b, 0) / ds1.length).toFixed(2))
    : 0;
  const avgDSCR2 = ds2.length
    ? Number((ds2.reduce((a, b) => a + b, 0) / ds2.length).toFixed(2))
    : 0;

  // ─── Loading / Error UI ───────────────────────────────────────────────
  if (loading) {
    return (
      <div className="p-6 text-center text-gray-600">
        Loading sensitivity analysis…
      </div>
    );
  }
  if (error) {
    return <div className="p-6 text-center text-red-600">{error}</div>;
  }

  // ─── Render Tables with Tailwind ──────────────────────────────────────
  return (
    <div className="p-6 space-y-8">
      <h1 className="text-2xl font-bold">Sensitivity Analysis</h1>

      {/* ── Base Table ───────────────────────────────────────────────── */}
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full bg-white border-collapse">
          <thead>
            <tr className="bg-gray-100">
              <th className="px-4 py-2 border text-left font-semibold">
                Parameter
              </th>
              {yrs.map((y) => (
                <th
                  key={y}
                  className="px-4 py-2 border text-center font-semibold"
                >
                  {y}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Total Sales (Net) from localStorage */}
            <tr>
              <td className="px-4 py-2 border">Total Sales (Net)</td>
              {salesNet.map((v, idx) => (
                <td key={idx} className="px-4 py-2 border text-center">
                  {v.toFixed(2)}
                </td>
              ))}
            </tr>

            {/* Cost of Materials Consumed from localStorage */}
            <tr className="bg-gray-50">
              <td className="px-4 py-2 border">Cost of Materials Consumed</td>
              {costs.map((v, idx) => (
                <td key={idx} className="px-4 py-2 border text-center">
                  {v.toFixed(2)}
                </td>
              ))}
            </tr>

            {/* Net Profit After Tax from API */}
            <tr>
              <td className="px-4 py-2 border">Net Profit</td>
              {npat.map((v, idx) => (
                <td key={idx} className="px-4 py-2 border text-center">
                  {v.toFixed(2)}
                </td>
              ))}
            </tr>

            {/* Depreciation (computed via Depreciation.jsx logic) */}
            <tr className="bg-gray-50">
              <td className="px-4 py-2 border">Depreciation</td>
              {depr.map((v, idx) => (
                <td key={idx} className="px-4 py-2 border text-center">
                  {v.toFixed(2)}
                </td>
              ))}
            </tr>

            {/* Interest On Loan (from API EMI summary) */}
            <tr>
              <td className="px-4 py-2 border">Interest On Loan</td>
              {iol.map((v, idx) => (
                <td key={idx} className="px-4 py-2 border text-center">
                  {v.toFixed(2)}
                </td>
              ))}
            </tr>

            {/* Net Profit Before Dep & Interest */}
            <tr className="bg-gray-100 font-semibold">
              <td className="px-4 py-2 border">
                Net Profit Before Dep &amp; Interest
              </td>
              {npdi.map((v, idx) => (
                <td key={idx} className="px-4 py-2 border text-center">
                  {v.toFixed(2)}
                </td>
              ))}
            </tr>

            {/* Repayment of Term Loan (principal) */}
            <tr>
              <td className="px-4 py-2 border">Repayment of Term Loan</td>
              {rla.map((v, idx) => (
                <td key={idx} className="px-4 py-2 border text-center">
                  {v.toFixed(2)}
                </td>
              ))}
            </tr>

            {/* Repayment of Interest (same as Interest On Loan) */}
            <tr className="bg-gray-50">
              <td className="px-4 py-2 border">Repayment of Interest</td>
              {iol.map((v, idx) => (
                <td key={idx} className="px-4 py-2 border text-center">
                  {v.toFixed(2)}
                </td>
              ))}
            </tr>

            {/* TOTAL (DEBTS) = principal + interest */}
            <tr className="bg-gray-100 font-semibold">
              <td className="px-4 py-2 border">TOTAL (DEBTS)</td>
              {deb.map((v, idx) => (
                <td key={idx} className="px-4 py-2 border text-center">
                  {v.toFixed(2)}
                </td>
              ))}
            </tr>

            {/* DSCR */}
            <tr className="bg-gray-50 font-semibold">
              <td className="px-4 py-2 border">DSCR</td>
              {ds.map((v, idx) => (
                <td key={idx} className="px-4 py-2 border text-center">
                  {v.toFixed(2)}
                </td>
              ))}
            </tr>

            {/* Average DSCR */}
            <tr className="bg-gray-100 font-semibold">
              <td className="px-4 py-2 border">Average DSCR</td>
              {yrs.map((_, idx) => (
                <td key={idx} className="px-4 py-2 border text-center">
                  {avgDSCR.toFixed(2)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      {/* ── Scenario 1 Table ───────────────────────────────────────────── */}
      <div className="mt-8 space-y-2">
        <h2 className="text-xl font-semibold">
          Scenario 1 (Selling price ↓ by {scenario1Pct}%)
        </h2>
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full bg-white border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="px-4 py-2 border text-left font-semibold">
                  Parameter
                </th>
                {yrs.map((y) => (
                  <th
                    key={y}
                    className="px-4 py-2 border text-center font-semibold"
                  >
                    {y}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Scenario 1: Total Sales (Net) */}
              <tr>
                <td className="px-4 py-2 border">Total Sales (Net)</td>
                {ss1.map((v, idx) => (
                  <td key={idx} className="px-4 py-2 border text-center">
                    {v.toFixed(2)}
                  </td>
                ))}
              </tr>

              {/* Cost of Materials Consumed (unchanged) */}
              <tr className="bg-gray-50">
                <td className="px-4 py-2 border">Cost of Materials Consumed</td>
                {costs.map((v, idx) => (
                  <td key={idx} className="px-4 py-2 border text-center">
                    {v.toFixed(2)}
                  </td>
                ))}
              </tr>

              {/* Profit After Tax (Scenario 1) */}
              <tr>
                <td className="px-4 py-2 border">Profit After Tax</td>
                {pat1.map((v, idx) => (
                  <td key={idx} className="px-4 py-2 border text-center">
                    {v.toFixed(2)}
                  </td>
                ))}
              </tr>

              {/* Depreciation */}
              <tr className="bg-gray-50">
                <td className="px-4 py-2 border">Depreciation</td>
                {depr.map((v, idx) => (
                  <td key={idx} className="px-4 py-2 border text-center">
                    {v.toFixed(2)}
                  </td>
                ))}
              </tr>

              {/* Interest On Loan */}
              <tr>
                <td className="px-4 py-2 border">Interest On Loan</td>
                {iol.map((v, idx) => (
                  <td key={idx} className="px-4 py-2 border text-center">
                    {v.toFixed(2)}
                  </td>
                ))}
              </tr>

              {/* Net Profit Before Dep & Interest (Scenario 1) */}
              <tr className="bg-gray-100 font-semibold">
                <td className="px-4 py-2 border">
                  Net Profit Before Dep &amp; Interest
                </td>
                {npdi1.map((v, idx) => (
                  <td key={idx} className="px-4 py-2 border text-center">
                    {v.toFixed(2)}
                  </td>
                ))}
              </tr>

              {/* Repayment of Term Loan */}
              <tr>
                <td className="px-4 py-2 border">Repayment of Term Loan</td>
                {rla.map((v, idx) => (
                  <td key={idx} className="px-4 py-2 border text-center">
                    {v.toFixed(2)}
                  </td>
                ))}
              </tr>

              {/* Repayment of Interest */}
              <tr className="bg-gray-50">
                <td className="px-4 py-2 border">Repayment of Interest</td>
                {iol.map((v, idx) => (
                  <td key={idx} className="px-4 py-2 border text-center">
                    {v.toFixed(2)}
                  </td>
                ))}
              </tr>

              {/* TOTAL (DEBTS) */}
              <tr className="bg-gray-100 font-semibold">
                <td className="px-4 py-2 border">TOTAL (DEBTS)</td>
                {deb.map((v, idx) => (
                  <td key={idx} className="px-4 py-2 border text-center">
                    {v.toFixed(2)}
                  </td>
                ))}
              </tr>

              {/* DSCR (Scenario 1) */}
              <tr className="bg-gray-50 font-semibold">
                <td className="px-4 py-2 border">DSCR</td>
                {ds1.map((v, idx) => (
                  <td key={idx} className="px-4 py-2 border text-center">
                    {v.toFixed(2)}
                  </td>
                ))}
              </tr>

              {/* Average DSCR (Scenario 1) */}
              <tr className="bg-gray-100 font-semibold">
                <td className="px-4 py-2 border">Average DSCR</td>
                {yrs.map((_, idx) => (
                  <td key={idx} className="px-4 py-2 border text-center">
                    {avgDSCR1.toFixed(2)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Scenario 2 Table ───────────────────────────────────────────── */}
      <div className="mt-8 space-y-2">
        <h2 className="text-xl font-semibold">
          Scenario 2 (Raw material cost ↑ by {scenario2Pct}%)
        </h2>
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full bg-white border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="px-4 py-2 border text-left font-semibold">
                  Parameter
                </th>
                {yrs.map((y) => (
                  <th
                    key={y}
                    className="px-4 py-2 border text-center font-semibold"
                  >
                    {y}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Total Sales (Net) */}
              <tr>
                <td className="px-4 py-2 border">Total Sales (Net)</td>
                {salesNet.map((v, idx) => (
                  <td key={idx} className="px-4 py-2 border text-center">
                    {v.toFixed(2)}
                  </td>
                ))}
              </tr>

              {/* Cost of Materials Consumed (Scenario 2) */}
              <tr className="bg-gray-50">
                <td className="px-4 py-2 border">Cost of Materials Consumed</td>
                {cm2.map((v, idx) => (
                  <td key={idx} className="px-4 py-2 border text-center">
                    {v.toFixed(2)}
                  </td>
                ))}
              </tr>

              {/* Profit After Tax (Scenario 2) */}
              <tr>
                <td className="px-4 py-2 border">Profit After Tax</td>
                {pat2.map((v, idx) => (
                  <td key={idx} className="px-4 py-2 border text-center">
                    {v.toFixed(2)}
                  </td>
                ))}
              </tr>

              {/* Depreciation */}
              <tr className="bg-gray-50">
                <td className="px-4 py-2 border">Depreciation</td>
                {depr.map((v, idx) => (
                  <td key={idx} className="px-4 py-2 border text-center">
                    {v.toFixed(2)}
                  </td>
                ))}
              </tr>

              {/* Interest On Loan */}
              <tr>
                <td className="px-4 py-2 border">Interest On Loan</td>
                {iol.map((v, idx) => (
                  <td key={idx} className="px-4 py-2 border text-center">
                    {v.toFixed(2)}
                  </td>
                ))}
              </tr>

              {/* Net Profit Before Dep & Interest (Scenario 2) */}
              <tr className="bg-gray-100 font-semibold">
                <td className="px-4 py-2 border">
                  Net Profit Before Dep &amp; Interest
                </td>
                {npdi2.map((v, idx) => (
                  <td key={idx} className="px-4 py-2 border text-center">
                    {v.toFixed(2)}
                  </td>
                ))}
              </tr>

              {/* Repayment of Term Loan */}
              <tr>
                <td className="px-4 py-2 border">Repayment of Term Loan</td>
                {rla.map((v, idx) => (
                  <td key={idx} className="px-4 py-2 border text-center">
                    {v.toFixed(2)}
                  </td>
                ))}
              </tr>

              {/* Repayment of Interest */}
              <tr className="bg-gray-50">
                <td className="px-4 py-2 border">Repayment of Interest</td>
                {iol.map((v, idx) => (
                  <td key={idx} className="px-4 py-2 border text-center">
                    {v.toFixed(2)}
                  </td>
                ))}
              </tr>

              {/* TOTAL (DEBTS) */}
              <tr className="bg-gray-100 font-semibold">
                <td className="px-4 py-2 border">TOTAL (DEBTS)</td>
                {deb.map((v, idx) => (
                  <td key={idx} className="px-4 py-2 border text-center">
                    {v.toFixed(2)}
                  </td>
                ))}
              </tr>

              {/* DSCR (Scenario 2) */}
              <tr className="bg-gray-50 font-semibold">
                <td className="px-4 py-2 border">DSCR</td>
                {ds2.map((v, idx) => (
                  <td key={idx} className="px-4 py-2 border text-center">
                    {v.toFixed(2)}
                  </td>
                ))}
              </tr>

              {/* Average DSCR (Scenario 2) */}
              <tr className="bg-gray-100 font-semibold">
                <td className="px-4 py-2 border">Average DSCR</td>
                {yrs.map((_, idx) => (
                  <td key={idx} className="px-4 py-2 border text-center">
                    {avgDSCR2.toFixed(2)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
